import { ClaimRecord, ClaimRecordStatus } from '@typesApp/ClaimRecord'
import { InsuranceStatusErrorCode, InsuranceUsage } from '@typesApp/ClaimData'
import { isProductInsuranceEligible } from './isProductInsuranceEligible'
import { isInsuranceUsageEnabled, isInsuranceUsageDisabled } from './insuranceMemberUtil'
import { ProductTypesEligibility } from '@typesApp/Product'
import { ToggleContext } from '@typesApp/WidgetContextHolder'

type ToggleState = {
  toggleOn: boolean
  allowOn: boolean
  allowSelectProduct?: boolean
}

type ToggleStateParams = {
  claimRecord?: ClaimRecord
  claimRecordDeleted?: boolean
}

type ToggleStateParamsForCart = ToggleStateParams & {
  variant?: ToggleContext['variant']
}

type ToggleMessageKeyParams = {
  initError: boolean | undefined
  productQuoteError: boolean | undefined
  claimRecordDeleted: boolean | undefined
  claimRecordStatus: ClaimRecordStatus | undefined
  insuranceStatusErrorCode: InsuranceStatusErrorCode | undefined
}

type ToggleEligibilityMessageKeyParams = {
  claimRecordStatus: ClaimRecordStatus | undefined
  claimRecordDeleted: boolean | undefined
  hasEligibleProduct: boolean | undefined
}

type ToggleMessageKeyForCartParams = {
  initError: boolean | undefined
  claimRecordDeleted: boolean | undefined
  useInsuranceStatus: InsuranceUsage | undefined
  insuranceStatusErrorCode: InsuranceStatusErrorCode | undefined
  isToggleClicked: boolean | undefined
}

export const getInsuranceToggleState = (params: ToggleStateParams): ToggleState => {
  const { claimRecord, claimRecordDeleted } = params
  const toggleOn = isToggleOn(claimRecord, claimRecordDeleted)
  const allowOn = isInsuranceUsageDisabled(claimRecord?.claimData?.useInsuranceStatus)

  return {
    toggleOn,
    allowOn,
    allowSelectProduct: false,
  }
}

const isToggleOn = (claimRecord: ClaimRecord | undefined, claimRecordDeleted?: boolean): boolean => {
  const insuranceUsage = claimRecord?.claimData?.useInsuranceStatus
  return !claimRecordDeleted && isInsuranceUsageEnabled(insuranceUsage)
}

export const getInsuranceToggleStateForCart = (params: ToggleStateParamsForCart): ToggleState => {
  const { claimRecord, claimRecordDeleted, variant } = params

  const hasBenefits =
    claimRecord?.status !== ClaimRecordStatus.NO_BENEFITS_AVAILABLE &&
    claimRecord?.claimData?.insuranceStatusErrorCode !== InsuranceStatusErrorCode.BENEFITS_UNAVAILABLE

  const countOfEligibleCartProducts = (claimRecord?.eligibleProducts ?? []).filter(isProductInsuranceEligible).length
  const toggleOn = isToggleOn(claimRecord, claimRecordDeleted)
  const allowOn = isInsuranceUsageDisabled(claimRecord?.claimData?.useInsuranceStatus)

  // hide select product for mini toggle
  const isMiniToggle = variant === 'mini'

  return {
    toggleOn,
    allowOn,
    allowSelectProduct: !isMiniToggle && hasBenefits && countOfEligibleCartProducts > 1 && toggleOn,
  }
}

export const getInsuranceToggleMessageKey = (params: ToggleMessageKeyParams): string | undefined => {
  const { claimRecordStatus, claimRecordDeleted, initError, productQuoteError, insuranceStatusErrorCode } = params
  if (claimRecordDeleted) return

  if (
    claimRecordStatus === ClaimRecordStatus.NO_BENEFITS_AVAILABLE ||
    insuranceStatusErrorCode === InsuranceStatusErrorCode.BENEFITS_UNAVAILABLE
  ) {
    return InsuranceStatusErrorCode.BENEFITS_UNAVAILABLE
  }

  if (productQuoteError) {
    return InsuranceStatusErrorCode.PRODUCT_QUOTE_FAILED
  }

  if (initError) {
    return InsuranceStatusErrorCode.INIT_ERROR
  }
}

export const getInsuranceToggleEligibilityMessageKey = (
  params: ToggleEligibilityMessageKeyParams
): string | undefined => {
  const { claimRecordStatus, claimRecordDeleted, hasEligibleProduct } = params

  if (!claimRecordStatus || claimRecordStatus === ClaimRecordStatus.NONE || claimRecordDeleted) return

  // Note: for non cart, if product is not eligible we show 'benefits not available' message
  if (!hasEligibleProduct) {
    return InsuranceStatusErrorCode.BENEFITS_UNAVAILABLE
  }
}

const IN_PROGRESS_ERROR_CODE = {
  SUFFIX: 'IN_PROGRESS',
  RELOAD_MESSAGE_KEY: 'APPLICATION_IN_PROGRESS',
  RETRY_MESSAGE_KEY: 'APPLICATION_IN_PROGRESS_RETRY',
} as const

export const getInsuranceToggleMessageKeyForCart = (params: ToggleMessageKeyForCartParams): string | undefined => {
  const { isToggleClicked, useInsuranceStatus, insuranceStatusErrorCode, claimRecordDeleted, initError } = params
  if (claimRecordDeleted) return

  if (insuranceStatusErrorCode && insuranceStatusErrorCode !== InsuranceStatusErrorCode.NONE) {
    const inProgress = insuranceStatusErrorCode.endsWith(IN_PROGRESS_ERROR_CODE.SUFFIX)
    if (inProgress) {
      // toggle was not clicked and is enabled, show reload page message
      if (!isToggleClicked && isInsuranceUsageEnabled(useInsuranceStatus)) {
        return IN_PROGRESS_ERROR_CODE.RELOAD_MESSAGE_KEY
      }

      // fallback with retry message
      return IN_PROGRESS_ERROR_CODE.RETRY_MESSAGE_KEY
    }

    return insuranceStatusErrorCode
  }

  if (initError) {
    return InsuranceStatusErrorCode.INIT_ERROR_ON_CART
  }
}

export const hasEligibleProductType = (productTypesEligibility: ProductTypesEligibility): boolean => {
  return productTypesEligibility?.productTypesEligibility?.some(item => item.benefitAvailability === 'SUPPORTED')
}
